import { useState, useMemo, useEffect, useRef, useTransition } from 'react';
import { trackPageView } from '@tasama/utils';
import type { StepperStep } from '@tasama/ui';
import { useTranslation } from '@tasama/i18n';
import {
  useCompanyTypeFormData,
  useCompanyTypeStore,
  type CompanyTypeStore,
} from '../../features/company-type/stores/company-type-store';
import {
  useStepManagementStore,
  type StepManagementStore,
} from '../stores/step-management.store';
import {
  getDefaultSteps,
  getPifProjectSteps,
  stepPathMap,
} from '../config/step-config';
import { env } from '../config/env';
import type { OnboardingStepData } from '../../types';

interface UseOnboardingNavigationInput {
  stepValidation: Record<string, boolean>;
  submitStepData: (_data: OnboardingStepData | FormData) => Promise<boolean>;
  clearError: () => void;
  isSubmitting: boolean;
  getCurrentStepFormData: (
    _stepId: string
  ) => Record<string, unknown> | FormData;
}

export function useOnboardingNavigation({
  stepValidation,
  submitStepData,
  clearError,
  isSubmitting,
  getCurrentStepFormData,
}: UseOnboardingNavigationInput) {
  const [isPending] = useTransition();
  const { t } = useTranslation('onboarding');

  // Internal step state - no URL changes
  // Initialize from persisted store or default to 'company-type'
  const persistedStep = useStepManagementStore(
    (state: StepManagementStore) => state.currentStep
  );
  const [internalStep, setInternalStep] = useState<string>(
    persistedStep?.id || 'company-type'
  );

  // Track step changes as virtual page views.
  // Skip the first render — the initial /onboarding entry is already captured by usePageTracking.
  const isFirstStepRender = useRef(true);
  useEffect(() => {
    if (isFirstStepRender.current) {
      isFirstStepRender.current = false;
      return;
    }
    const stepPath = stepPathMap[internalStep] ?? `/${internalStep}`;
    trackPageView('/onboarding' + stepPath);
  }, [internalStep]);

  // Compute steps internally to avoid circular dependency with useOnboardingSteps
  const companyTypeFormData = useCompanyTypeFormData();
  const isPifProject =
    companyTypeFormData.companyType === 'pif-owned' &&
    companyTypeFormData.companyStatus === 'PIF_UNDER_DEVELOPMENT';
  const steps: StepperStep[] = useMemo(() => {
    return isPifProject ? getPifProjectSteps(t) : getDefaultSteps(t);
  }, [isPifProject, t]);

  // Store reset methods
  const clearCompanyTypeStore = useCompanyTypeStore(
    (state: CompanyTypeStore) => state.clearStore
  );
  const clearAllOtherStores = useCompanyTypeStore(
    (state: CompanyTypeStore) => state.clearAllOtherStores
  );
  const resetStepManagementStore = useStepManagementStore(
    (state: StepManagementStore) => state.resetSteps
  );

  const markStepCompleted = useStepManagementStore(
    (state: StepManagementStore) => state.markStepCompleted
  );
  const unmarkStepCompleted = useStepManagementStore(
    (state: StepManagementStore) => state.unmarkStepCompleted
  );
  const isNavigatingBack = useStepManagementStore(
    (state: StepManagementStore) => state.isNavigatingBack
  );
  const setIsNavigatingBack = useStepManagementStore(
    (state: StepManagementStore) => state.setIsNavigatingBack
  );

  useEffect(() => {
    const isOnFirstStep = internalStep === 'company-type';
    if (isOnFirstStep) {
      // Only reset if not navigating back (i.e., on page reload or initial load)
      if (!isNavigatingBack) {
        clearCompanyTypeStore();
        clearAllOtherStores();
      } else {
        // Reset the navigation flag after checking
        setIsNavigatingBack(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    internalStep,
    clearCompanyTypeStore,
    setIsNavigatingBack,
    clearAllOtherStores,
  ]);

  // Handle completion action - clear all stores and redirect to main route
  const handleDone = () => {
    // Reset all Zustand stores to clear persisted data
    clearCompanyTypeStore();
    clearAllOtherStores();
    resetStepManagementStore();

    // Reset internal step to first step
    window.location.href = env.PORTAL_APP_BASE_URL;
  };

  // Handle next button click with API call
  const handleContinue = async () => {
    clearError();

    // Collect form data from current step
    const currentStepData = getCurrentStepFormData(internalStep);

    if (currentStepData) {
      // @ts-ignore
      const success = await submitStepData(currentStepData);

      // Only proceed to next step if API call was successful
      if (success) {
        // Mark current step as completed AFTER successful API call
        const currentStepPath = stepPathMap[internalStep] || '/';
        markStepCompleted(currentStepPath);

        const currentStepIndex = steps.findIndex(
          (step) => step.id === internalStep
        );
        const nextStepIndex = currentStepIndex + 1;

        // Check if there's a next step
        if (nextStepIndex < steps.length) {
          const nextStepId = steps[nextStepIndex]?.id;
          if (!nextStepId) return;
          setInternalStep(nextStepId);
        } else if (internalStep === 'service-needs') {
          // Move to confirmation after service-needs
          setInternalStep('confirmation');
        }
      }
    }
  };

  // Handle back button click
  const handleBack = () => {
    const currentStepIndex = steps.findIndex(
      (step) => step.id === internalStep
    );
    const previousStepIndex = currentStepIndex - 1;

    // Check if there's a previous step
    if (previousStepIndex >= 0) {
      // Set navigation flag to true before navigating back
      setIsNavigatingBack(true);

      const currentStepPath = stepPathMap[internalStep] || '/';
      unmarkStepCompleted(currentStepPath);

      const prevStepId = steps[previousStepIndex]?.id;
      if (prevStepId) {
        const prevStepPath = stepPathMap[prevStepId] || '/';
        unmarkStepCompleted(prevStepPath);
        setInternalStep(prevStepId);
      }
    }
  };

  // Determine if continue button should be disabled based on form validation and submission state
  const isContinueDisabled = useMemo(() => {
    const currentStepPath = stepPathMap[internalStep] || '/';

    // Check if current step is valid
    const isCurrentStepValid =
      stepValidation[currentStepPath as keyof typeof stepValidation];

    // Disable if form is not valid or if submission is in progress
    return !isCurrentStepValid || isPending || isSubmitting;
  }, [internalStep, stepValidation, isPending, isSubmitting]);

  return {
    internalStep,
    handleContinue,
    handleBack,
    handleDone,
    isContinueDisabled,
    isPending,
  };
}
